/**
 * SignatureSection
 * 
 * Renders signature section with optional signature images.
 * Handles both text-only and image-based signatures.
 */

import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { CONTROL_CARD_CONFIG, CONTROL_CARD_TEXT } from '../../config'
import { ImageRenderer } from '../../infrastructure'
import type { ControlCardInspection } from '../../infrastructure'

/**
 * Signature section renderer
 */
export class SignatureSection {
  private readonly imageRenderer: ImageRenderer

  constructor(
    private readonly config: typeof CONTROL_CARD_CONFIG = CONTROL_CARD_CONFIG
  ) {
    this.imageRenderer = new ImageRenderer(config)
  }

  /**
   * Render signature section
   * Returns the Y position after rendering
   */
  async render(
    doc: jsPDF,
    inspection: ControlCardInspection,
    startY: number
  ): Promise<number> {
    // Check if we have signature images
    const hasSignatures =
      inspection.performedBySignature || inspection.responsiblePersonSignature

    if (hasSignatures) {
      return await this.renderWithImages(doc, inspection, startY)
    } else {
      return this.renderWithoutImages(doc, inspection, startY)
    }
  }

  /**
   * Render signature section with signature images
   */
  private async renderWithImages(
    doc: jsPDF,
    inspection: ControlCardInspection,
    startY: number
  ): Promise<number> {
    const tableStartX = this.config.layout.tableStartX
    const tableWidth = this.config.layout.tableWidth

    try {
      // Store cell positions for signature images
      let leftSignatureCell: { x: number; y: number; width: number; height: number } | null = null
      let rightSignatureCell: { x: number; y: number; width: number; height: number } | null = null

      // Render table and capture signature cell positions
      autoTable(doc, {
        body: [
          [CONTROL_CARD_TEXT.no.headers.performedBy, CONTROL_CARD_TEXT.no.headers.responsible],
          ['', ''], // Signature image row
          [inspection.performedBy || 'Inspector Name', inspection.responsiblePerson || 'Responsible Person'],
        ],
        startY,
        margin: { left: tableStartX },
        theme: 'grid',
        styles: {
          lineColor: this.config.colors.border,
          lineWidth: this.config.borders.standard,
        },
        bodyStyles: {
          fontSize: this.config.typography.body.size,
          cellPadding: this.config.spacing.cellPadding,
          halign: 'center',
          textColor: this.config.colors.text,
        },
        columnStyles: {
          0: { cellWidth: tableWidth / 2 },
          1: { cellWidth: tableWidth / 2 },
        },
        didParseCell: (data: any) => {
          if (data.row.index === 0) {
            data.cell.styles.fillColor = this.config.colors.white
            data.cell.styles.fontStyle = 'bold'
            data.cell.styles.halign = 'center'
          }
          if (data.row.index === 1) {
            data.cell.styles.minCellHeight = this.config.spacing.signatureMinHeight
          }
          if (data.row.index === 2) {
            data.cell.styles.halign = 'center'
          }
        },
        didDrawCell: (data) => {
          // Capture signature cell positions (sync, no await)
          if (data.row.index === 1 && data.cell && data.cell.width && data.cell.height) {
            const cellInfo = {
              x: data.cell.x,
              y: data.cell.y,
              width: data.cell.width,
              height: data.cell.height,
            }

            if (data.column.index === 0) {
              leftSignatureCell = cellInfo
            } else if (data.column.index === 1) {
              rightSignatureCell = cellInfo
            }
          }
        },
        willDrawCell: (data) => {
          if (data.cell) {
            data.cell.styles.lineWidth = this.config.borders.standard
          }
        },
      })

      // NOW add signature images after table is complete
      if (leftSignatureCell && inspection.performedBySignature) {
        await this.imageRenderer.addSignatureImage(
          doc,
          inspection.performedBySignature,
          leftSignatureCell
        )
      }

      if (rightSignatureCell && inspection.responsiblePersonSignature) {
        await this.imageRenderer.addSignatureImage(
          doc,
          inspection.responsiblePersonSignature,
          rightSignatureCell
        )
      }

      return doc.lastAutoTable.finalY
    } catch (error) {
      console.error('❌ Error rendering signature section with images:', error)
      // Fallback to signature section without images
      return this.renderWithoutImages(doc, inspection, startY)
    }
  }

  /**
   * Render signature section without signature images
   */
  private renderWithoutImages(
    doc: jsPDF,
    inspection: ControlCardInspection,
    startY: number
  ): number {
    const tableStartX = this.config.layout.tableStartX
    const tableWidth = this.config.layout.tableWidth

    autoTable(doc, {
      body: [
        [CONTROL_CARD_TEXT.no.headers.performedBy, CONTROL_CARD_TEXT.no.headers.responsible],
        ['', ''], // Empty row for signatures
        [inspection.performedBy || 'Inspector Name', inspection.responsiblePerson || 'Responsible Person'],
      ],
      startY,
      margin: { left: tableStartX },
      theme: 'grid',
      styles: {
        lineColor: this.config.colors.border,
        lineWidth: this.config.borders.standard,
      },
      bodyStyles: {
        fontSize: this.config.typography.body.size,
        cellPadding: this.config.spacing.cellPadding,
        halign: 'center',
        textColor: this.config.colors.text,
      },
      columnStyles: {
        0: { cellWidth: tableWidth / 2 },
        1: { cellWidth: tableWidth / 2 },
      },
      didParseCell: (data: any) => {
        if (data.row.index === 0) {
          data.cell.styles.fillColor = this.config.colors.white
          data.cell.styles.fontStyle = 'bold'
          data.cell.styles.halign = 'center'
        }
        if (data.row.index === 1) {
          data.cell.styles.minCellHeight = this.config.spacing.signatureFallbackHeight
        }
        if (data.row.index === 2) {
          data.cell.styles.halign = 'center'
        }
      },
      willDrawCell: (data) => {
        if (data.cell) {
          data.cell.styles.lineWidth = this.config.borders.standard
        }
      },
    })

    return doc.lastAutoTable.finalY
  }
}
